import React, { useState, useEffect } from 'react';
import { Plus, Package, AlertCircle } from 'lucide-react';
import apiClient from '../../utils/apiClient';
import PageContainer from '../layout/PageContainer';

interface Ingredient {
  id: number;
  name: string;
  unit: string;
  current_stock: number;
  category: string;
}

interface StockInHistory {
  id: number;
  ingredient_name: string;
  quantity: number;
  unit: string;
  created_at: string;
}

const SimpleStockInput: React.FC = () => {
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [selectedId, setSelectedId] = useState<number>(0);
  const [quantity, setQuantity] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<StockInHistory[]>([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    fetchIngredients();
    fetchHistory();
  }, []);

  const fetchIngredients = async () => {
    try {
      const [ingredientsRes, stocksRes] = await Promise.all([
        apiClient.get('/ingredients'),
        apiClient.get('/simple-inventory/stocks')
      ]);
      const stockMap = new Map<number, number>();
      for (const stock of (stocksRes.data || [])) {
        const stockId = Number(stock?.id);
        const stockValue = Number(stock?.current_stock);
        if (Number.isFinite(stockId) && Number.isFinite(stockValue)) {
          stockMap.set(stockId, stockValue);
        }
      }
      const mergedIngredients = (ingredientsRes.data || []).map((ing: any) => {
        const mappedStock = stockMap.get(Number(ing.id));
        const normalizedStock = Number.isFinite(mappedStock as number)
          ? Number(mappedStock)
          : Number(ing.current_stock);
        return {
          ...ing,
          current_stock: Number.isFinite(normalizedStock) ? normalizedStock : 0
        };
      });
      setIngredients(mergedIngredients);
    } catch (error) {
      console.error('材料取得エラー:', error);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await apiClient.get('/simple-inventory/history?limit=10');
      setHistory(res.data);
    } catch (error) {
      console.error('履歴取得エラー:', error);
    }
  };

  const handleSubmit = async () => {
    if (!selectedId) {
      alert('食材を選択してください');
      return;
    }

    if (!quantity || parseFloat(quantity) <= 0) {
      alert('数量を正しく入力してください');
      return;
    }

    setLoading(true);

    try {
      await apiClient.post('/simple-inventory/stock-in', {
        ingredient_id: selectedId,
        quantity: parseFloat(quantity)
      });

      alert('✅ 仕入れを登録しました');
      setQuantity('');
      setSelectedId(0);
      await fetchIngredients(); // 在庫再取得
      await fetchHistory(); // 履歴再取得
    } catch (error) {
      console.error('仕入れ登録エラー:', error);
      alert('❌ 登録に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const selectedIngredient = ingredients.find(i => i.id === selectedId);
  const currentStock = selectedIngredient?.current_stock || 0;
  const newStock = quantity ? currentStock + parseFloat(quantity) : currentStock;

  return (
    <PageContainer>
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Package className="text-blue-600" />
            🍖 仕入れ入力
          </h1>
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-sm"
          >
            {showHistory ? '入力画面に戻る' : '履歴を見る'}
          </button>
        </div>

        {!showHistory ? (
          <>
            {/* 仕入れ入力フォーム */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <div className="space-y-6">
                {/* 食材選択 */}
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">
                    📦 食材を選択
                  </label>
                  <select
                    value={selectedId}
                    onChange={(e) => setSelectedId(parseInt(e.target.value))}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value={0}>選択してください</option>
                    {ingredients.map(ing => (
                      <option key={ing.id} value={ing.id}>
                        {ing.name} ({ing.unit}) - カテゴリ: {ing.category || '未分類'}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 数量入力 */}
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">
                    🔢 仕入れ数量
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0.0"
                      disabled={!selectedId}
                    />
                    <div className="px-4 py-3 bg-gray-100 rounded-lg text-gray-700 font-medium min-w-[80px] text-center">
                      {selectedIngredient?.unit || '-'}
                    </div>
                  </div>
                </div>

                {/* 登録ボタン */}
                <button
                  onClick={handleSubmit}
                  disabled={loading || !selectedId || !quantity}
                  className="w-full bg-blue-600 text-white py-4 rounded-lg font-bold text-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  <Plus size={24} />
                  {loading ? '登録中...' : '仕入れを登録する'}
                </button>
              </div>
            </div>

            {/* 在庫プレビュー */}
            {selectedId > 0 && (
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6 shadow-md">
                <h3 className="text-lg font-bold mb-4 text-blue-900">📊 在庫状況</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4">
                    <div className="text-sm text-gray-600 mb-1">現在の在庫</div>
                    <div className="text-3xl font-bold text-gray-900">
                      {currentStock.toFixed(1)} <span className="text-lg text-gray-600">{selectedIngredient?.unit}</span>
                    </div>
                  </div>
                  {quantity && parseFloat(quantity) > 0 && (
                    <div className="bg-white rounded-lg p-4 border-2 border-green-400">
                      <div className="text-sm text-gray-600 mb-1">登録後の在庫</div>
                      <div className="text-3xl font-bold text-green-600">
                        {newStock.toFixed(1)} <span className="text-lg text-green-600">{selectedIngredient?.unit}</span>
                      </div>
                      <div className="text-xs text-green-600 mt-1">
                        (+{parseFloat(quantity).toFixed(1)} {selectedIngredient?.unit})
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 使い方ガイド */}
            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="text-blue-600 mt-0.5" size={20} />
                <div className="text-sm text-blue-900">
                  <p className="font-bold mb-1">💡 使い方</p>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>仕入れた食材を選択します</li>
                    <li>仕入れた数量を入力します</li>
                    <li>「仕入れを登録する」ボタンをクリックします</li>
                  </ol>
                  <p className="mt-2 text-xs">※ POSレジで商品が売れると、自動的に在庫が減ります</p>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* 仕入れ履歴 */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold mb-4">📋 最近の仕入れ履歴（直近10件）</h3>
              {history.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Package size={48} className="mx-auto mb-2 opacity-50" />
                  <p>まだ仕入れ履歴がありません</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {history.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <Package className="text-blue-600" size={20} />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{item.ingredient_name}</div>
                          <div className="text-sm text-gray-500">
                            {new Date(item.created_at).toLocaleString('ja-JP')}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xl font-bold text-green-600">
                          +{item.quantity.toFixed(1)} <span className="text-sm">{item.unit}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </PageContainer>
  );
};

export default SimpleStockInput;
